import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { useMeasure } from '@hivebrite/react-hooks';

import type {
  ImageDimensions,
  ImagePosition,
  PositionHandler,
} from 'hb-builder/components/BuilderComponents/page/single/PageImage/types';
import { useDeviceModeContext } from 'hb-builder/hooks';
import type { PageImageItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/pageImageItemAttributess';

import ResizableImage from './components/ResizableImage';
import { getXValue } from './utils/helpers';

const getLeftValue = ({ horizontalAlign, wrapperWidth, imageWidth = 0 }) => {
  if (horizontalAlign === 'center') {
    return (wrapperWidth - imageWidth) / 2;
  }

  if (horizontalAlign === 'right') {
    return wrapperWidth - imageWidth;
  }

  return 0;
};

interface ResizableImageContainerProps {
  attributes: PageImageItemAttributes;
  getInitialDimensions: (
    imageDimensions: ImageDimensions,
    wrapperWidth: number,
  ) => void;
  handleResize: ({
    value,
    wrapperWidth,
  }: {
    value: PageImageItemAttributes;
    wrapperWidth: number;
  }) => void;
  isResizing: boolean;
  positionsHandler?: PositionHandler[];
}

const ResizableImageContainer = ({
  attributes: {
    constructionColumnWidth = 1,
    height,
    horizontalAlign,
    imageFullWidth,
    imageSrc,
    left,
    top,
    width,
  },
  getInitialDimensions,
  handleResize,
  isResizing,
  positionsHandler,
}: ResizableImageContainerProps) => {
  const [ref, { height: wrapperHeight, width: wrapperWidth }] =
    useMeasure<HTMLDivElement>();
  const { isMobileMode } = useDeviceModeContext();

  const [dimensions, setDimensions] = useState<
    Pick<PageImageItemAttributes, 'height' | 'width'>
  >({
    height,
    width,
  });
  const [position, setPosition] = useState<ImagePosition>({
    x: left || 0,
    y: top || 0,
  });

  useEffect(() => {
    if (imageFullWidth) {
      setDimensions((prev) => {
        if (prev.width !== wrapperWidth) {
          return { ...prev, width: wrapperWidth };
        }

        return prev;
      });
    } else {
      setDimensions((prev) => {
        if (prev.width !== width) {
          return { height, width };
        }

        return prev;
      });
    }

    setPosition((prev) => ({
      ...prev,
      x: imageFullWidth
        ? 0
        : left ||
          getLeftValue({
            horizontalAlign,
            wrapperWidth,
            imageWidth: dimensions.width,
          }),
    }));
  }, [
    dimensions,
    height,
    horizontalAlign,
    imageFullWidth,
    left,
    width,
    wrapperWidth,
  ]);

  useEffect(() => {
    setPosition({
      x: left || 0,
      y: top || 0,
    });
  }, [left, top]);

  useEffect(() => {
    if (
      (!width || !height || wrapperWidth !== constructionColumnWidth) &&
      imageSrc
    ) {
      const loadImage = () => {
        const imgNode = new window.Image();

        imgNode.onload = (e) => {
          const target = e.target as HTMLImageElement;

          // If the image is not loaded properly, we try again
          if (!target.width || !target.height) {
            loadImage();

            return;
          }

          // In builder v1 documents the constructionColumnWidth is not set so it's taking the default value of 1 but the new computed size is then too big because not divided
          // Instead, we keep the original size for the render, and the constructionColumnWidth will be set to an actual value (wrapperWidth)
          const isBuilderV1Image = constructionColumnWidth === 1;

          const newHeight =
            !isBuilderV1Image && height
              ? (height * wrapperWidth) / constructionColumnWidth
              : target?.width < wrapperWidth
                ? target?.height
                : (wrapperWidth * (target?.height || 1)) / (target?.width || 1);

          const newWidth =
            !isBuilderV1Image && width
              ? (width * wrapperWidth) / constructionColumnWidth
              : target?.width < wrapperWidth
                ? target?.width
                : wrapperWidth;

          // We don't want to set dimensions if they are not valid (they may be 0 when the wrapperWidth is not yet set properly (0))
          if (!newHeight || !newWidth) {
            return;
          }

          const newDimensions = {
            height: newHeight,
            width: newWidth,
          };

          getInitialDimensions(newDimensions, wrapperWidth);
          setDimensions(newDimensions);
        };

        imgNode.src = imageSrc;
      };

      loadImage();

      return;
    }

    const ratioWidth = ((width || 0) * wrapperWidth) / constructionColumnWidth;
    const ratioHeight =
      ((height || 0) * wrapperWidth) / constructionColumnWidth;

    setDimensions({
      height: ratioHeight,
      width: ratioWidth,
    });

    const ratioTop = ((top || 0) * wrapperWidth) / constructionColumnWidth;
    const ratioLeft = ((left || 0) * wrapperWidth) / constructionColumnWidth;

    setPosition({
      x: getXValue({
        horizontalAlign,
        defaultValue: ratioLeft,
        imageWidth: ratioWidth,
        wrapperWidth,
      }),
      y: ratioTop,
    });
  }, [left, top, horizontalAlign, wrapperWidth]);

  useEffect(() => {
    // When on mobile mode, we need to recalculate the dimensions if the height or width is set in the JSON (when going back in the history stack for example)
    // Otherwise, the image will be displayed with the desktop dimensions
    if (
      isMobileMode &&
      height &&
      width &&
      wrapperWidth &&
      constructionColumnWidth &&
      wrapperWidth !== constructionColumnWidth
    ) {
      const newHeight = (height * wrapperWidth) / constructionColumnWidth;
      const newWidth = (width * wrapperWidth) / constructionColumnWidth;

      const newDimensions = {
        height: newHeight,
        width: newWidth,
      };

      getInitialDimensions(newDimensions, wrapperWidth);
      setDimensions(newDimensions);

      return;
    }

    setDimensions({ height, width });
  }, [height, width]);

  const handleChange = ({
    resizeDirection,
    value,
  }: {
    resizeDirection: string;
    value: ImagePosition & Pick<PageImageItemAttributes, 'height' | 'width'>;
  }) => {
    if (!value) {
      return null;
    }

    setPosition((prev) => {
      // left value cannot be greater than the image width or negative
      const XValue =
        value.x < wrapperWidth ? (value.x > 0 ? value.x : 0) : prev.x;

      const newPosition = {
        x: XValue,
        y: value.y,
      };

      // position surplus should be subtracted from dimension
      const heightValue =
        value.y > 0 ? value.height || 0 : (value.height || 0) + value.y;
      const widthValue =
        value.x > 0 ? value.width || 0 : (value.width || 0) + value.x;

      const newDimensions = {
        height: heightValue,
        width:
          widthValue + XValue > wrapperWidth
            ? wrapperWidth - XValue
            : widthValue,
      };

      setDimensions(newDimensions);

      handleResize?.({
        value: {
          ...newPosition,
          ...newDimensions,
          imageFullWidth: newDimensions.width === wrapperWidth,
          horizontalAlign:
            resizeDirection !== 'top' && resizeDirection !== 'bottom'
              ? 'center'
              : horizontalAlign,
          verticalAlign: undefined,
        },
        wrapperWidth,
      });

      return newPosition;
    });
  };

  return (
    <ImgWrapper ref={ref}>
      <ImgContent
        height={(dimensions.height || 0) + (position.y || 0)}
        width={
          (dimensions.width || 0) + (horizontalAlign ? 0 : position.x || 0)
        }
      >
        <ResizableImage
          dimensions={dimensions}
          isResizing={isResizing}
          onChange={handleChange}
          position={position}
          positionsHandler={positionsHandler}
          wrapperDimensions={{ wrapperHeight, wrapperWidth }}
        />
      </ImgContent>
    </ImgWrapper>
  );
};

export default ResizableImageContainer;

const ImgContent = styled(Box).attrs(() => ({
  display: 'block',
}))`
  position: relative;
`;

const ImgWrapper = styled(Box).attrs(() => ({
  width: '100%',
}))``;
